import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { contentEnhancementTracker, EnhancementOperation } from '@/services/contentEnhancementTracker';
import { Loader2, CheckCircle, XCircle, Clock, Activity, AlertCircle } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { supabase } from '@/integrations/supabase/client';

export const EnhancementOperationsMonitor: React.FC = () => {
  const [operations, setOperations] = useState<EnhancementOperation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOperations();

    // Subscribe to real-time updates
    const unsubscribe = contentEnhancementTracker.subscribeToOperations((updatedOp) => {
      setOperations(prev => {
        const index = prev.findIndex(op => op.id === updatedOp.id);
        if (index >= 0) {
          const newOps = [...prev];
          newOps[index] = updatedOp;
          return newOps;
        }
        return [updatedOp, ...prev];
      });
    });

    return () => unsubscribe();
  }, []);

  const loadOperations = async () => {
    setLoading(true);
    
    // Clean up stale operations first (older than 30 minutes and still "in_progress")
    await cleanupStaleOperations();
    
    const ops = await contentEnhancementTracker.getRecentOperations(20);
    setOperations(ops);
    setLoading(false);
  };

  const cleanupStaleOperations = async () => {
    try {
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
      
      // Delete operations completed with 0 progress (likely duplicates/errors)
      await supabase
        .from('content_enhancement_operations')
        .delete()
        .eq('status', 'completed')
        .eq('processed_items', 0)
        .eq('successful_items', 0);
      
      // Get all in_progress operations
      const { data: inProgressOps } = await supabase
        .from('content_enhancement_operations')
        .select('id, started_at')
        .eq('status', 'in_progress')
        .order('started_at', { ascending: false });

      if (inProgressOps && inProgressOps.length > 1) {
        // Keep only the most recent, mark others as failed
        const opsToFail = inProgressOps.slice(1).map(op => op.id);
        
        if (opsToFail.length > 0) {
          const { error } = await supabase
            .from('content_enhancement_operations')
            .update({ 
              status: 'failed',
              error_message: 'Duplicate operation - only most recent kept',
              completed_at: new Date().toISOString()
            })
            .in('id', opsToFail);
          
          if (error) {
            console.error('Failed to cleanup duplicate operations:', error);
          } else {
            console.log(`✅ Cleaned up ${opsToFail.length} duplicate operations`);
          }
        }
      }

      // Also mark operations older than 5 minutes as failed
      const { error: timeoutError } = await supabase
        .from('content_enhancement_operations')
        .update({ 
          status: 'failed',
          error_message: 'Operation timed out - exceeded 5 minutes without progress',
          completed_at: new Date().toISOString()
        })
        .eq('status', 'in_progress')
        .lt('started_at', fiveMinutesAgo);
      
      if (timeoutError) {
        console.error('Failed to cleanup timed out operations:', timeoutError);
      }
    } catch (error) {
      console.error('Error cleaning up stale operations:', error);
    }
  };

  const cancelAllOperations = async () => {
    try {
      const { error } = await supabase
        .from('content_enhancement_operations')
        .update({ 
          status: 'failed',
          error_message: 'Cancelled by user',
          completed_at: new Date().toISOString()
        })
        .eq('status', 'in_progress');
      
      if (!error) {
        await loadOperations();
      }
    } catch (error) {
      console.error('Error cancelling operations:', error);
    }
  };

  const getStatusIcon = (status: EnhancementOperation['status']) => {
    switch (status) {
      case 'in_progress':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: EnhancementOperation['status']) => {
    const variants = {
      in_progress: 'default',
      completed: 'default',
      failed: 'destructive',
      cancelled: 'secondary'
    } as const;

    return (
      <Badge variant={variants[status]} className="capitalize">
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  const getOperationTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      bulk_enhance: 'Bulk Content Enhancement',
      single_enhance: 'Single Module Enhancement',
      quiz_generate: 'Quiz Generation',
      practice_generate: 'Practice Lab Generation'
    };
    return labels[type] || type;
  };

  const formatDuration = (startedAt: string, completedAt?: string) => {
    const start = new Date(startedAt);
    const end = completedAt ? new Date(completedAt) : new Date();
    const durationMs = end.getTime() - start.getTime();
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // Only show the most recent active operation
  const activeOperations = operations
    .filter(op => op.status === 'in_progress')
    .sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime())
    .slice(0, 1); // Only show the most recent one

  // Filter out in_progress from history since it's shown in active section
  const historyOperations = operations.filter(op => op.status !== 'in_progress');

  return (
    <div className="space-y-4">
      {/* Active Operations */}
      {activeOperations.length > 0 && (
        <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/20">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-blue-600" />
                Active Operations
              </CardTitle>
              <CardDescription>Currently running enhancement operations</CardDescription>
            </div>
            <Button 
              variant="destructive" 
              size="sm"
              onClick={cancelAllOperations}
            >
              Cancel All
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {activeOperations.map((operation) => {
              const progress = operation.total_items > 0 
                ? (operation.processed_items / operation.total_items) * 100 
                : 0;

              return (
                <div key={operation.id} className="space-y-2 p-4 rounded-lg bg-background border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(operation.status)}
                      <span className="font-medium">{getOperationTypeLabel(operation.operation_type)}</span>
                    </div>
                    {getStatusBadge(operation.status)}
                  </div>
                  
                  <Progress value={progress} className="h-2" />
                  
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                      {operation.processed_items} / {operation.total_items} items
                      ({operation.successful_items} success, {operation.failed_items} failed)
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDuration(operation.started_at)}
                    </span>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Recent Operations History */}
      <Card>
        <CardHeader>
          <CardTitle>Enhancement History</CardTitle>
          <CardDescription>Recent content enhancement operations</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-3">
              {historyOperations.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No completed operations yet
                </p>
              ) : (
                historyOperations.map((operation) => {
                  const progress = operation.total_items > 0 
                    ? (operation.processed_items / operation.total_items) * 100 
                    : 0;
                  const hasErrors = operation.failed_items > 0 || operation.error_message;

                  return (
                    <div key={operation.id} className="p-4 rounded-lg border space-y-2 hover:bg-accent/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(operation.status)}
                          <span className="font-medium text-sm">
                            {getOperationTypeLabel(operation.operation_type)}
                          </span>
                        </div>
                        {getStatusBadge(operation.status)}
                      </div>
                      
                      {operation.status === 'in_progress' && (
                        <Progress value={progress} className="h-1" />
                      )}
                      
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>
                          {operation.successful_items} / {operation.total_items} successful
                          {operation.failed_items > 0 && ` (${operation.failed_items} failed)`}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDuration(operation.started_at, operation.completed_at)}
                        </span>
                      </div>

                      <p className="text-xs text-muted-foreground">
                        {new Date(operation.started_at).toLocaleString()}
                      </p>

                      {/* Error Details Section */}
                      {hasErrors && (
                        <Collapsible>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="w-full justify-start text-destructive h-auto py-1 px-2">
                              <AlertCircle className="h-3 w-3 mr-2" />
                              <span className="text-xs">View Error Details ({operation.failed_items} failures)</span>
                            </Button>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="mt-2 p-2 bg-destructive/10 rounded-md">
                            {operation.error_message && (
                              <p className="text-xs text-destructive mb-1">
                                <strong>Error:</strong> {operation.error_message}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground">
                              Failed {operation.failed_items} modules. Check edge function logs for details.
                            </p>
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};
